import Api from 'fou-api'
import ApiBinaryDef from 'fou-api/definitions/Binary'
import { BinaryHelper, ErrorToasterBinary, getUploadContentType } from 'src/utils/helper/binaries'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'
import VuexErrors from 'src/vuex/modules/errors'
import Errors from 'src/utils/helper/errors'
import { Render } from 'src/utils/helper/render'
import Content from 'src/assets/i18n/en.json'
import get from 'lodash/get'
import { calculateMD5 } from 'src/utils/helper/misc'

const $t = i18n().global.t;
export default {
  namespaced: true,

  state: {
    binaries: [],
    Md5BinaryRequest: [],
    newBinaries: [],
    getting: false,
    uploading: false,
    errorMessage: get(Content, 'pages.submissions.components.upload.uploader.error_messages.error'),
    errorType: get(Content, 'pages.submissions.components.upload.uploader.error_messages.errortype'),
    invalidExtensionsError: get(Content, 'pages.submissions.components.upload.uploader.error_messages.invalid_file_error'),
    invalidExtensionsType: get(Content, 'pages.submissions.components.upload.uploader.error_messages.invalid_file_type'),
    duplicateErrorMsg: get(Content, 'pages.submissions.components.upload.uploader.error_messages.duplicate_error'),
    duplicateErrorType: get(Content, 'pages.submissions.components.upload.uploader.error_messages.duplicate_error_type'),
    updateModal: true,
    binaryValResult: false,
    uploadBinaryPartialValidation: true,
    isFirstUpload: false,
    fileSizeExceedError: get(Content, 'pages.submissions.components.upload.uploader.error_messages.file_size_exceed'),
    fileSizeExceedType: get(Content, 'pages.submissions.components.upload.uploader.error_messages.file_size_exceed_type'),
    uploadedFonts: [],
    maxLimitFileError: get(Content, 'pages.submissions.components.upload.uploader.error_messages.max_limit_file_error'),
    scanStatus: '',
    filesWithFalseAvStatus: [],
    isliveModalCancelDisable: false,
    maxFilesWithFalseAvStatus: 0,
    malwareFoundError: get(Content, 'malwareScan.toastMessage'),
    showMalwareToastError: true
  },

  getters: {
    list: state => {
      return BinaryHelper.buildBinaries(state.binaries)
    },
    newList: state => {
      return BinaryHelper.buildBinaries(state.newBinaries)
    },
    uploading: state => {
      return state.uploading
    },
    hasAllMeta: state => {
      /**
       * If a result has metaData its status is 'fontInfoCompleted'.
       * If not it is 'opened'.
       * Returns true if all binaries have metaData.
       */
      const hasBinaries = state.binaries.length > 0
      const incomplete = state.binaries.some(b => {
        return b.fontInfoComplete === false && (b.validationResult?.errorLevel || '') !== 'ERROR'
      })
      return hasBinaries && !incomplete
    },
    hasSomeMeta: state => {
      /**
       * Returns true if one binary has metaData.
       */
      const hasBinaries = state.binaries.length > 0
      return hasBinaries && state.binaries.some(b => b.fontInfoComplete === true || b.hasCriticalErrors === true)
    },
    hasAllValidated: state => {
      /**
       * Returns true if all binaries are validated.
       */
      const hasBinaries = state.binaries.length > 0
      const validated = state.binaries.map(b => {
        const complete = b.validationResult?.progress?.complete
        return typeof complete === 'boolean' && complete === true
      })
      return hasBinaries && validated.indexOf(false) === -1
    },
    hasAllSfdms: state => {
      /**
       * Returns true if all binaries have completed SFDMS processing.
       * ERROR binaries are treated as complete, matching hasAllMeta behavior.
       */
      const hasBinaries = state.binaries.length > 0
      const incomplete = state.binaries.some(b => {
        const sectionsComplete = b?.validationResult?.progress?.sectionsComplete || []
        const hasSfdms = sectionsComplete.includes('onSfdmsComplete')
        return !hasSfdms && (b.validationResult?.errorLevel || '') !== 'ERROR'
      })
      return hasBinaries && !incomplete
    },
    hasAllPSNames: state => {
      const hasPS = state.binaries.every(b => b.hasOwnProperty('fontInfo') && b.fontInfo.hasOwnProperty('psName'))
      return hasPS
    },
    hasAllStyleNames: state => {
      const hasStyleName = state.binaries.every(
        b => b.hasOwnProperty('fontInfo') && b.fontInfo.hasOwnProperty('styleName')
      )
      return hasStyleName
    },
    hasSomeValidated: state => {
      /**
       * Returns true if one binariy are validated.
       */
      const hasBinaries = state.binaries.length > 0
      return hasBinaries && state.binaries.some(b => (b.validationResult?.progress?.complete === true))
    },
    getBinaryFontRenderSrc: state => {
      return (id, name, size = 20, color = '00000', rt = false) => {
        /**
         * Find binary by id and get md5.
         * Returns Uri of rendered fontface of a binary.
         */
        const result = state.binaries.filter(b => b.id === id)[0]
        const md5 = result?.md5Checksum
        return typeof md5 === 'string' && typeof id === 'string'
          ? Render.get({
            md5,
            name: encodeURI(name),
            size,
            fg: color,
            rt
          })
          : null
      }
    },
    hasAllSortInfo: state => {
      return state.binaries.every(
        b =>
          b.hasOwnProperty('fontInfo') &&
          b.fontInfo.hasOwnProperty('weightClass') &&
          b.fontInfo.weightClass !== '' &&
          b.fontInfo.hasOwnProperty('isItalic')
      )
    },
    getUpdateModal: state => {
      return state.updateModal
    },
    getBinaryValResult: state => {
      return state.binaryValResult
    },
    getUploadBinaryPartialResult: state => {
      return state.uploadBinaryPartialValidation
    },
    isFirstUpload: state => {
      return state.isFirstUpload
    },
    getUploadedFonts: state => {
      return state.uploadedFonts
    },
    getExistingFonts: state => {
      return state.existingFonts
    },
    getScanStatus: state => {
      return state.scanStatus
    },
    getFilesWithFalseAvStatus: state => {
      return state.filesWithFalseAvStatus
    },
    getLiveModalCancelButton: state => {
      return state.isliveModalCancelDisable
    },
    getMaxFilesWithFalseAvStatus: state => {
      return state.maxFilesWithFalseAvStatus
    }
  },

  mutations: {
    add(state, binary) {
      const exists = state.binaries.some(b => b.id === binary.id)
      if (!exists) {
        state.binaries.push(ApiBinaryDef.build(binary))
      }
    },
    addNewBinaries(state, binary) {
      const exists = state.newBinaries.some(b => b.id === binary.id)
      if (!exists) {
        state.newBinaries.push(ApiBinaryDef.build(binary))
      }
    },
    deleteBins(state, ids) {
      const binaries = state.binaries.filter(val => {
        return ids.indexOf(val.id) === -1
      })
      state.binaries = binaries
    },
    deleteNewBinaries(state, id) {
      state.newBinaries.some((b, idx) => {
        if (b.id === id) {
          state.newBinaries.splice(idx, 1)
          return true
        }
      })
    },
    emptyNewBinaries(state) {
      state.newBinaries = []
    },
    clear(state) {
      state.binaries = []
      // state.filesWithFalseAvStatus = []
    },
    clearNewBinaries(state, id) {
      state.newBinaries = state.newBinaries.filter(item => item.id !== id)
    },
    getAllStart(state) {
      state.getting = true
    },
    getAllComplete(state) {
      state.getting = false
    },
    uploadStart(state) {
      state.uploading = true
    },
    uploadComplete(state) {
      state.uploading = false
    },
    syncOrder(state, _idxOf) {
      state.binaries.sort((a, b) => {
        return _idxOf(a.id) > _idxOf(b.id) ? 1 : -1
      })
    },
    setUpdatePopup(state, value) {
      state.updateModal = value
    },
    closeUpdatePopup(state, value) {
      state.updateModal = value
    },
    binaryValidationResult(state, value) {
      state.binaryValResult = value
    },
    uploadBinaryValidationResult(state, value) {
      state.uploadBinaryPartialValidation = value
    },
    updateIsFirstUpload(state) {
      state.isFirstUpload = true
    },
    setUploadedFonts(state, value) {
      state.uploadedFonts.push(value);
    },
    removeUploadedFonts(state, value) {
      state.uploadedFonts = state.uploadedFonts.filter((item) => item !== value);
    },
    emptyUploadedFonts(state) {
      state.uploadedFonts = [];
    },
    setScanStatus(state, value) {
      state.scanStatus = value
    },
    setFilesWithFalseAvStatus(state, files) {
      state.filesWithFalseAvStatus = files;
    },
    clearFilesWithFalseAvStatus(state) {
      state.filesWithFalseAvStatus = [];
    },
    clearMd5BinaryRequest(state) {
      state.Md5BinaryRequest = [];
    },
    setLiveModalCancelButton(state, val) {
      state.isliveModalCancelDisable = val;
    },
    setMaxFilesWithFalseAvStatus(state, val) {
      state.maxFilesWithFalseAvStatus = val;
    },
    setShowMalwareToastError(state, val) {
      state.showMalwareToastError = val;
    }
  },

  actions: {
    upload({ commit, state, getters }, options = {}) {
      commit('setLiveModalCancelButton', true)
      commit('setShowMalwareToastError', true);
      commit('setScanStatus', '');
      commit('clearFilesWithFalseAvStatus');
      return new Promise((resolve, reject) => {
        if (state.uploading) {
          return resolve();
        }
        const existingBinariesMd5 = state?.binaries.map(item => item.md5Checksum);
        const maxFileSize = 52 * 1024 * 1024;
        let uploaded = 0;
        let validFilesToUpload = [];
        let invalidFiles = [];

        // Early exit check: if no files submitted but Md5BinaryRequest exists, this is likely a duplicate call
        // This prevents multiple API calls when upload actions are triggered with 0 files
        if ((!options?.submits || options.submits.length === 0) && state.Md5BinaryRequest.length > 0) {
          return resolve();
        }
        if (options?.submits.length > 200) {
          const err = Errors.create({ message: state.maxLimitFileError, name: 'FileNumberExceeds' });
          commit('errors/add', err);
          return;
        }
        function done() {
          uploaded++;
          if (uploaded >= options?.submits.length) {
            resolve();
          }
        }

        function finalizeUpload() {
          commit('uploadComplete');
          commit('uploadBinaryValidationResult', true);
          commit('updateIsFirstUpload', true);
          setTimeout(() => {
            commit('binaryValidationResult', false);
          }, 10000);
        }

        function handleUpload(file, presignedUrl, retryCount = 0) {
          const contentType = getUploadContentType(file);
          return fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': contentType,
            },
          })
            .then((response) => {
              if (!response.ok && retryCount < 1) {
                return handleUpload(file, presignedUrl, retryCount + 1);
              }
              if (!response.ok) {
                throw new Error('Upload failed after retry');
              }
            })
            .catch((error) => {
              if (retryCount < 1) {
                return handleUpload(file, presignedUrl, retryCount + 1);
              }
              throw error;
            });
        }

        function handleFileValidation(file) {
          return new Promise((resolve, reject) => {
            if (!file) {
              const err = Errors.create({ message: state.errorMessage + ': ' + file.name, name: state.errorType });
              commit('closeUpdatePopup', false);
              commit('uploadBinaryValidationResult', false);
              commit('errors/add', err);
              commit('uploadComplete');
              done();
              return reject(err);
            }

            const allowedExtensions = ['otf', 'ttf', 'ttfv', 'tt'];
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(fileExtension)) {
              const err = Errors.create({ message: 'Invalid file extension: ' + file.name, name: state.invalidExtensionsType });
              commit('closeUpdatePopup', false);
              commit('uploadBinaryValidationResult', false);
              commit('uploadComplete');
              done();
              return reject(err);
            }

            const reader = new FileReader();
            reader.onload = (event) => {
              const buffer = new Uint8Array(event.target.result);
              const fileType = getFileType(buffer);
              if (!['font/ttf', 'font/otf'].includes(fileType)) {
                const err = Errors.create({ message: 'Invalid file type: ' + file.name, name: 'InvalidFileTypeError' });
                commit('closeUpdatePopup', false);
                commit('uploadBinaryValidationResult', false);
                commit('errors/add', err);
                commit('uploadComplete');
                commit('binaryValidationResult', false);
                done();
                return reject(err);
              }

              resolve(file);
            };

            if (file.size > maxFileSize) {
              const err = Errors.create({ message: state.fileSizeExceedError + ': ' + file.name, name: state.fileSizeExceedType });
              commit('closeUpdatePopup', false);
              commit('uploadBinaryValidationResult', false);
              commit('uploadComplete');
              done();
              return reject(err);
            }

            reader.readAsArrayBuffer(file.slice(0, 4));
          });
        }

        function getFileType(buffer) {
          const header = Array.from(buffer)
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
          switch (header) {
            case '00010000': // TTF file
            case '4c504943':
              return 'font/ttf';
            case '4f54544f': // OTF file
              return 'font/otf';
            default:
              return 'unknown';
          }
        }

        function processFileUpload(file) {
          return new Promise((resolve, reject) => {
            commit('uploadStart');
            calculateMD5(file)
              .then((md5) => {
                if (existingBinariesMd5.includes(md5) || state.uploadedFonts.includes(md5)) {
                  const err = Errors.create({ message: state.duplicateErrorMsg + ': ' + file.name, name: state.duplicateErrorType });
                  commit('closeUpdatePopup', false);
                  commit('uploadBinaryValidationResult', false);
                  commit('uploadComplete');
                  done();
                  return reject(err);
                } else {
                  commit('setUploadedFonts', md5);
                  commit('submissions/setIsSubmissionInProgress', true, { root: true });
                  validFilesToUpload.push({ file, md5 });
                  resolve();
                }
              })
              .catch((error) => {
                commit('submissions/setIsSubmissionInProgress', false, { root: true });
                const err = Errors.create(error)
                reject(err);
              });
          });
        }

        const validationPromises = options.submits.map((s) =>
          handleFileValidation(s.file)
            .then((file) => processFileUpload(file).then(() => { }).catch((error) => {
              if (error.name === state.duplicateErrorType)
                invalidFiles.push({ fileName: file.name, errorName: state.duplicateErrorType });
            }))
            .catch((error) => {
              invalidFiles.push({ fileName: s.file.name, errorName: error.name });
            })
        );

        Promise.allSettled(validationPromises)
          .then(() => {
            const { fileSizeExceeded, invalidFileExtensionFiles, duplicatedFiles } = ErrorToasterBinary(invalidFiles, state.fileSizeExceedType, state.invalidExtensionsType, state.duplicateErrorType);

            if (fileSizeExceeded?.length) {
              const err = Errors.create({ message: state.fileSizeExceedError + '\u2022' + "\xa0\xa0" + fileSizeExceeded.join("\n\u2022\xa0\xa0"), name: state.errorType });
              commit('errors/add', err);
            }

            if (invalidFileExtensionFiles?.length) {
              const err = Errors.create({ message: state.invalidExtensionsError + '\u2022' + "\xa0\xa0" + invalidFileExtensionFiles.join("\n\u2022\xa0\xa0"), name: state.invalidExtensionsType });
              commit('errors/add', err);
            }

            if (duplicatedFiles?.length) {
              const err = Errors.create({ message: state.duplicateErrorMsg + '\u2022' + "\xa0\xa0" + duplicatedFiles.join("\n\u2022\xa0\xa0"), name: state.duplicateErrorType });
              commit('errors/add', err);
            }

            if (validFilesToUpload.length === 0) {
              done();
              return;
            }
            const md5List = validFilesToUpload.map((fileData) => fileData.md5 + '.' + fileData.file.name.split('.').pop());
            return Api.Binaries.createPresignedUrl(options.id, { keys: md5List });
          })
          .then((presignedUrlResponse) => {
            if (!presignedUrlResponse) {
              return;
            }
            const presignedUrls = presignedUrlResponse.data.result;
            const uploadPromises = validFilesToUpload.map((fileData) => {

              let presignedUrlData = presignedUrls.find(
                (urlData) => urlData.md5 === fileData.md5
              );

              // Check if presignedUrlData exists and isPresignedGenerated is false
              if (presignedUrlData && presignedUrlData.isPresignedGenerated === false) {
                state.Md5BinaryRequest.push({
                  md5: fileData.md5,
                  fontFileName: fileData.file.name,
                  mimeType: fileData.file.name.split('.').pop(),
                  fileSize: fileData.file.size
                });
              } else {
                presignedUrlData = presignedUrls.find(
                  (urlData) => urlData.md5 === fileData.md5 + '.' + fileData.file.name.split('.').pop()
                );
              }
              const presignedUrl = presignedUrlData ? presignedUrlData.signedUrl : null;

              // Check if presignedUrl is available start uploading
              if (presignedUrl) {
                return handleUpload(fileData.file, presignedUrl)
                  .then(() => {
                    state.Md5BinaryRequest.push({
                      md5: fileData.md5,
                      fontFileName: fileData.file.name,
                      mimeType: fileData.file.name.split('.').pop(),
                      fileSize: fileData.file.size
                    });
                    const prevValidationResults = getters.getUploadBinaryPartialResult;
                    commit('uploadBinaryValidationResult', prevValidationResults);
                    commit('closeUpdatePopup', true);
                    done();
                  })
                  .catch((error) => {
                    let err = Errors.create({ message: `Binary upload failed for ${fileData.file.name}`, name: 'UploadRetryError' });
                    commit('removeUploadedFonts', fileData.md5);
                    commit('closeUpdatePopup', false);
                    commit('uploadBinaryValidationResult', false);
                    commit('errors/add', err);
                    commit('uploadComplete');
                    done();
                  });
              } else {
                return Promise.resolve(); // No presigned URL, resolve the promise
              }
            });
            return Promise.all(uploadPromises);
          })
          .then(() => {
            // Early exit if no valid files were processed in this call and no Md5BinaryRequest
            if ((!options?.submits || options.submits.length === 0) && state.Md5BinaryRequest.length === 0) {
              return;
            }

            // Call the API after all uploads are completed
            if (state.Md5BinaryRequest.length > 0) {
              // CRITICAL: Copy Md5BinaryRequest and clear immediately to prevent concurrent actions from reusing same data
              // This ensures each upload action gets its own snapshot of the data
              const md5BinaryRequestCopy = [...state.Md5BinaryRequest];
              commit('clearMd5BinaryRequest'); // Clear immediately after copying

              commit('updateIsFirstUpload', true)
              return Api.Binaries.uploadBinary(options.id, { binaries: md5BinaryRequestCopy })
                .then(response => {
                  if (response.data.binaries.length > 0) {
                    const binaries = response.data.binaries;
                    const allComplete = binaries.every(binary => binary.validationResult.progress.complete);

                    // Temporary workaround to address the live popup style issue
                    if (allComplete) {
                      binaries[0].validationResult.progress.complete = false;
                    }
                    // Commit all binaries and their validation results
                    binaries.forEach(binary => {
                      commit('add', binary);
                      commit('addNewBinaries', binary);
                      const prevValidationResults = getters.getUploadBinaryPartialResult;
                      commit(
                        'uploadBinaryValidationResult',
                        prevValidationResults &&
                        binary.validationResult?.progress?.complete &&
                        binary.validationResult?.validations?.length === 0
                      );
                      commit('binaryValidationResult',
                        binary.validationResult?.progress?.complete &&
                        binary.validationResult?.validations?.length === 0
                      )
                    });
                    // Check AV status and update state
                    const filesWithFalseAvStatus = binaries
                      .filter(binary => binary.avStatus == 'false')
                      .map(binary => binary.fileName);
                    const allFilesHaveTrueAvStatus = binaries.every(binary => binary.avStatus === 'true');
                    commit('setMaxFilesWithFalseAvStatus', 0);
                    if (filesWithFalseAvStatus.length > 0) {
                      commit('setShowMalwareToastError', false);
                      Toaster.notifyMalwareWarning($t('malwareScan.toastMessage'))
                      commit('setFilesWithFalseAvStatus', filesWithFalseAvStatus);
                      commit('setScanStatus', false);
                    } else if (allFilesHaveTrueAvStatus) {
                      if (state.scanStatus === '') {
                        commit('clearFilesWithFalseAvStatus');
                        commit('setScanStatus', true);
                        commit('setLiveModalCancelButton', false);
                      }
                    }
                    commit('closeUpdatePopup', true);
                  }
                  done();
                })
                .catch(error => {
                  let err = Errors.create({ error });
                  commit('uploadBinaryValidationResult', false);
                  commit('errors/add', err);
                  commit('uploadComplete');
                  done();
                });
            }
          })
          .then(() => {
            finalizeUpload();
            commit('submissions/setIsSubmissionInProgress', false, { root: true });
            resolve();
          })
          .catch((error) => {
            commit('binaryValidationResult', false);
            commit('submissions/setIsSubmissionInProgress', false, { root: true });
            commit('setLiveModalCancelButton', false)
            console.error(error);
            resolve();
          });
      });
    },

    getAll({ commit, state }, options = {}) {
      options = {
        submissionId: null,
        force: false,
        ...options
      }

      return new Promise(resolve => {
        /**
         * Dont call if in progress.
         */
        if (state.getting) {
          resolve()
        } else {
          /**
           * Commit start as event.
           */
          commit('getAllStart')
          /**
           * Dont call if already has binaries.
           */
          if (state.binaries.length > 0 && !options.force) {
            commit('getAllComplete')
            resolve()
          } else {
            /**
             * Call.
             */
            Api.Binaries.getBinaries(options.submissionId)
              .then(response => {
                commit('clear')
                commit('setLiveModalCancelButton', false)
                let binaryValResults = true
                if (response?.data?.binaries) {
                  const binaries = response.data.binaries;
                  const filesWithFalseAvStatus = [];

                  binaries.forEach(item => {
                    commit('add', item);
                    if (item.avStatus == 'false') {
                      filesWithFalseAvStatus.push(item.fileName);
                    }
                    binaryValResults = binaryValResults && item.validationResult.progress.complete && item.validationResult.validations.length === 0;
                  });
                  const allFilesHaveTrueAvStatus = binaries.every(binary => binary.avStatus === 'true');
                  if (filesWithFalseAvStatus.length > 0 && state.maxFilesWithFalseAvStatus <= filesWithFalseAvStatus.length) {
                    if (state.showMalwareToastError) {
                      Toaster.notifyMalwareWarning($t('malwareScan.toastMessage'))
                      commit('setShowMalwareToastError', false);
                    }
                    commit('setMaxFilesWithFalseAvStatus', filesWithFalseAvStatus.length);
                    commit('setFilesWithFalseAvStatus', filesWithFalseAvStatus);
                    commit('setScanStatus', false);
                  } else if (allFilesHaveTrueAvStatus) {
                    if (state.scanStatus === '') {
                      commit('setScanStatus', true);
                      commit('clearFilesWithFalseAvStatus');
                    }
                  }
                }
                if (response.data.binaries.length == 0) binaryValResults = false
                if (state.isFirstUpload === true) {
                  commit('binaryValidationResult', binaryValResults)
                  commit('updateIsFirstUpload', true)
                  setTimeout(() => {
                    commit('binaryValidationResult', false)
                  }, 3000)
                }
                commit('getAllComplete')
                resolve()
              })
              .catch(error => {
                commit('errors/add', Errors.create(error))
                commit('updateIsFirstUpload', true)
                commit('setLiveModalCancelButton', false)
                commit('getAllComplete')
                resolve()
              })
          }
        }
      })
    },
    deleteBinaries({ commit, state }, options = {}) {
      return new Promise((resolve, reject) => {
        /*
         * Commit immediately to change views.
         * Requires:
         * options.id (submissionId)
         * options.binary
         */
        const ids = options.binaries.map(v => v.id)
        commit('deleteBins', ids)
        options.binaries.forEach(binary => {
          commit('removeUploadedFonts', binary.md5Checksum);
        });
        Api.Binaries.deleteBinaries(options.id, { binaries: ids })
          .then(() => {
            resolve()
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    }
  },

  modules: {
    errors: VuexErrors
  }
}